import { base64ToBytes, parseRevision, type DocxEdit, type DocxRevision } from "../api/docxEdits";
import { extractFileBlocks, generatedRevision, isDocxRevision, revisionChain } from "../api/fileBlocks";
import { chatRequest, reasoningParams } from "../api/rcpClient";
import { answerModel, type Config } from "./config";
import { conversationFileBlocks, findOriginal } from "./originals";

// Vérification d'une révision Word à la fin de la réponse (19.10.2026).
//
// Le modèle cite de mémoire les passages à remplacer, sur des contextes de
// 100 k jetons : il lui arrive de mal relire (« the cause of project » pour
// « the research project »), puis de « corriger » sa propre erreur de lecture.
// Le moteur refuse alors le remplacement, à raison. Plutôt que de laisser ce
// refus à l'usager, on fait un essai à blanc sur le document d'origine et on
// renvoie au modèle chaque passage introuvable, avec le passage le plus proche
// du document : il recale sa modification sur le texte exact, ou l'abandonne
// si le document est déjà juste. Un seul tour, sans le document entier (rapide,
// peu coûteux). Ce qu'il renvoie repasse par le même moteur, avec la même
// tolérance stricte : il ne peut toujours pas abîmer le document.

const REPAIR_TIMEOUT_MS = 60_000;

const REPAIR_SYSTEM = `Tu as proposé des modifications d'un document Word, sous forme de remplacements « find → replace ». Certaines n'ont pas pu être appliquées : leur texte « find » n'existe pas tel quel dans le document — tu l'as probablement mal recopié ou mal relu. Pour chacune, on te donne le passage du document qui s'en rapproche le plus.

Pour chaque modification, dans l'ordre, réponds :
- si elle a toujours lieu d'être sur ce passage : {"find": "<copie EXACTE du passage du document>", "replace": "<ce même passage, modifié>"}. Le « find » doit être assez long pour être unique dans un long document : un membre de phrase d'au moins six mots autour de la modification, jamais un mot seul ;
- si le document dit déjà ce qu'il faut (tu avais mal lu), ou si aucun passage ne correspond : null.

Ne change pas le sens de la modification d'origine et n'en invente pas d'autre. Réponds UNIQUEMENT par un tableau JSON, un élément par modification, sans texte autour.`;

export interface RepairReport {
  /** Le message, bloc de révision mis à jour et note ajoutée ; inchangé sinon. */
  content: string;
  /** Résumé pour le journal d'activité, null si rien n'a été vérifié. */
  summary: string | null;
}

interface Verdict {
  edit: DocxEdit;
  /** Remplacement recalé par le modèle, ou null s'il l'abandonne. */
  repaired: DocxEdit | null;
  /** Ce que dit le document à cet endroit. */
  closest: string | null;
}

/** Tableau JSON de la réponse : un élément par modification, null = abandon. */
export function parseRepair(raw: string, n: number): (DocxEdit | null)[] | null {
  const start = raw.indexOf("[");
  const end = raw.lastIndexOf("]");
  if (start < 0 || end <= start) return null;
  let data: unknown;
  try {
    data = JSON.parse(raw.slice(start, end + 1));
  } catch {
    return null;
  }
  if (!Array.isArray(data) || data.length !== n) return null;
  return data.map((item) => {
    if (!item || typeof item !== "object") return null;
    const { find, replace } = item as { find?: unknown; replace?: unknown };
    return typeof find === "string" && find.trim() && typeof replace === "string" ? { find, replace } : null;
  });
}

/** Corps JSON du bloc, une modification par ligne (lisible dans « Contenu »). */
function revisionBody(revision: DocxRevision, edits: DocxEdit[]): string {
  const lines = edits.map((e) => `  ${JSON.stringify({ find: e.find, replace: e.replace })}`);
  const cont = revision.continue ? `"continue": true, ` : "";
  return `{"source": ${JSON.stringify(revision.source)}, ${cont}"edits": [\n${lines.join(",\n")}\n]}`;
}

/** Le message contient-il une révision Word à vérifier ? */
export function hasDocxRevision(content: string): boolean {
  // Un document généré plus haut n'a pas de fichier d'origine à vérifier.
  return extractFileBlocks(content).some((b) => b.complete && isDocxRevision(b) && !generatedRevision(b, conversationFileBlocks()));
}

const quote = (s: string) => `« ${s.length > 140 ? `${s.slice(0, 137)}…` : s} »`;

/** Vérifie chaque révision Word du message et fait recaler par le modèle les
 *  remplacements introuvables. Ne lève jamais (sauf annulation) : en cas de
 *  panne, le message part tel quel et l'avertissement au téléchargement dira
 *  ce qui manque. */
export async function repairDocxRevisions(content: string, cfg: Config, signal?: AbortSignal): Promise<RepairReport> {
  const summaries: string[] = [];
  let out = content;
  for (const block of extractFileBlocks(content)) {
    if (!block.complete || !isDocxRevision(block)) continue;
    // Un document généré plus haut : son texte est dans la conversation, les
    // passages y sont recopiés tels quels — et findOriginal, faute de mieux,
    // rendrait un Word fourni sans rapport.
    if (generatedRevision(block, conversationFileBlocks())) continue;
    const revision = parseRevision(block.body)!;
    const chain = revisionChain(block, conversationFileBlocks());
    const original = findOriginal(chain.source);
    if (!original || !out.includes(block.body)) continue;
    try {
      const { reviseDocx } = await import("../api/docxRevise");
      // Une révision qui prolonge la précédente se vérifie sur celle-ci.
      let bytes = base64ToBytes(original);
      for (const earlier of chain.earlier) {
        const prev = reviseDocx(bytes, earlier);
        if (prev.applied > 0) bytes = prev.bytes;
      }
      const first = reviseDocx(bytes, revision.edits, { dryRun: true });
      if (first.failed.length === 0) continue;

      const verdicts = await askRepair(first.failed, first.closest, cfg, signal);
      if (!verdicts) continue;
      // Un recalage n'est gardé que s'il s'applique vraiment ; sinon la
      // modification d'origine reste, et l'avertissement la signalera.
      const proposals = verdicts.filter((v) => v.repaired).map((v) => v.repaired!);
      const check = proposals.length > 0 ? reviseDocx(bytes, proposals, { dryRun: true }) : null;
      // Recalé mais sans effet (le document dit déjà ce qu'il propose) : c'est
      // un abandon qui ne dit pas son nom.
      const kept = (v: Verdict) => !!v.repaired && !check!.failed.includes(v.repaired) && !check!.unchanged.includes(v.repaired);
      const fixed = verdicts.filter(kept);
      const dropped = verdicts.filter((v) => !v.repaired || check!.unchanged.includes(v.repaired));

      const edits = revision.edits.flatMap((e) => {
        const v = verdicts.find((x) => x.edit === e);
        if (!v) return [e];
        if (dropped.includes(v)) return [];
        return fixed.includes(v) ? [v.repaired!] : [e];
      });
      out = out.replace(block.body, revisionBody(revision, edits));

      const notes: string[] = [];
      if (fixed.length > 0) {
        notes.push(`${fixed.length} modification${fixed.length > 1 ? "s" : ""} recalée${fixed.length > 1 ? "s" : ""} sur le texte exact du document`);
      }
      for (const v of dropped) {
        notes.push(
          `modification abandonnée : ${quote(v.edit.find)}${v.closest ? `, le document dit ${quote(v.closest)}` : ", passage absent du document"}`,
        );
      }
      if (notes.length > 0) {
        out += `\n\n_Vérification de ${block.name} : ${notes.join(" ; ")}._`;
        summaries.push(
          `${block.name} : ${first.failed.length} passage${first.failed.length > 1 ? "s" : ""} introuvable${first.failed.length > 1 ? "s" : ""}, ${fixed.length} recalé${fixed.length > 1 ? "s" : ""}, ${dropped.length} abandonné${dropped.length > 1 ? "s" : ""}`,
        );
      }
    } catch (e) {
      if (signal?.aborted) throw e;
      console.warn("[docxRepair] vérification impossible, révision laissée telle quelle :", e);
    }
  }
  return { content: out, summary: summaries.length > 0 ? `Vérification du Word — ${summaries.join(" ; ")}` : null };
}

async function askRepair(
  failed: DocxEdit[],
  closest: Map<DocxEdit, { text: string }>,
  cfg: Config,
  externalSignal?: AbortSignal,
): Promise<Verdict[] | null> {
  const list = failed
    .map((e, i) => {
      const near = closest.get(e)?.text;
      return [
        `${i + 1}. Ta modification : find ${JSON.stringify(e.find)} → replace ${JSON.stringify(e.replace)}`,
        `   Passage le plus proche du document : ${near ? JSON.stringify(near) : "aucun passage proche"}`,
      ].join("\n");
    })
    .join("\n\n");

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REPAIR_TIMEOUT_MS);
  const onAbort = () => controller.abort();
  externalSignal?.addEventListener("abort", onAbort);
  try {
    const model = answerModel(cfg);
    const data = await chatRequest(
      {
        model,
        max_tokens: 4000,
        ...reasoningParams(model, "bas"),
        temperature: 0,
        messages: [
          { role: "system", content: REPAIR_SYSTEM },
          { role: "user", content: list },
        ],
      },
      cfg,
      controller.signal,
    );
    const parsed = parseRepair(data.choices?.[0]?.message?.content ?? "", failed.length);
    if (!parsed) return null;
    return failed.map((edit, i) => ({ edit, repaired: parsed[i], closest: closest.get(edit)?.text ?? null }));
  } finally {
    clearTimeout(timeout);
    externalSignal?.removeEventListener("abort", onAbort);
  }
}
